import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { AlertCircle, CheckCircle2, Home, MailCheck, RefreshCw } from 'lucide-react';

import { useAuth } from '../global-components/src';
import { errorMessage } from '../utils/TextManagerTwo';
import { PACOCA_API_URL } from '../global-components/src/utils/vars';
import './verify-email.css';

type VerificationStatus = 'loading' | 'success' | 'error';

const isVerificationUrl = (rawUrl: string | null): rawUrl is string => {
  if (!rawUrl) return false;

  try {
    const verificationUrl = new URL(rawUrl);
    const apiUrl = new URL(PACOCA_API_URL);
    const apiPath = apiUrl.pathname.replace(/\/$/, '');

    return (
      verificationUrl.origin === apiUrl.origin &&
      new RegExp(`^${apiPath}/verify-email/[^/]+/[^/]+$`).test(
        verificationUrl.pathname,
      )
    );
  } catch {
    return false;
  }
};

export default function VerifyEmail() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const verifyUrl = searchParams.get('url');
  const { token, setUser } = useAuth();
  const [status, setStatus] = useState<VerificationStatus>('loading');
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  const verifyEmail = useCallback(async () => {
    if (!isVerificationUrl(verifyUrl)) {
      setStatus('error');
      setMessage(t('emailVerification.invalidLink'));
      return;
    }

    setStatus('loading');
    setMessage('');

    try {
      const response = await axios.get(verifyUrl, {
        headers: {
          Accept: 'application/json',
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });

      setUser(previousUser => {
        if (!previousUser) return previousUser;

        return {
          ...previousUser,
          email_verified_at: new Date(),
        };
      });
      setMessage(response.data?.message || t('emailVerification.successFallback'));
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setMessage(
        errorMessage(err) ||
          t('emailVerification.expiredLink'),
      );
    }
  }, [setUser, t, token, verifyUrl]);

  useEffect(() => {
    void verifyEmail();
  }, [verifyEmail]);

  const isLoading = status === 'loading';
  const isSuccess = status === 'success';

  return (
    <main className="email-verification-page">
      <Helmet>
        <title>{t('emailVerification.pageTitle')}</title>
        <meta
          name="description"
          content={t('emailVerification.pageDescription')}
        />
      </Helmet>

      <section
        className={`email-verification-card email-verification-card--${status}`}
        aria-labelledby="email-verification-title"
      >
        <div className="email-verification-icon" aria-hidden="true">
          {isLoading && <span className="email-verification-spinner" />}
          {isSuccess && <CheckCircle2 />}
          {status === 'error' && <AlertCircle />}
        </div>

        <p className="email-verification-eyebrow">Paçoca</p>
        <h1 id="email-verification-title">
          {isLoading && t('emailVerification.loadingTitle')}
          {isSuccess && t('emailVerification.successTitle')}
          {status === 'error' && t('emailVerification.errorTitle')}
        </h1>
        <p className="email-verification-description" aria-live="polite">
          {isLoading && t('emailVerification.loadingText')}
          {!isLoading && message}
        </p>

        {isSuccess && (
          <>
            <div className="email-verification-success-note">
              <MailCheck size={18} aria-hidden="true" />
              <span>{t('emailVerification.successNote')}</span>
            </div>
            <button
              className="email-verification-button"
              type="button"
              onClick={() => navigate('/')}
            >
              <Home size={18} aria-hidden="true" />
              {t('emailVerification.home')}
            </button>
          </>
        )}

        {status === 'error' && (
          <div className="email-verification-actions">
            <button
              className="email-verification-button"
              type="button"
              onClick={() => void verifyEmail()}
            >
              <RefreshCw size={18} aria-hidden="true" />
              {t('emailVerification.retry')}
            </button>
            <button
              className="email-verification-link"
              type="button"
              onClick={() => navigate('/')}
            >
              {t('emailVerification.backHome')}
            </button>
          </div>
        )}
      </section>
    </main>
  );
}
